// Engine adapters for the transcription lab (controllers/transcription_lab_controller.js).
// Each one turns an engine's websocket protocol into caption lines for the lab:
//   lab.updateLine(key, { finalText, partialText, closed })   one key per sentence
//   lab.engineError(message), lab.engineEnded(), lab.engineClosed(code)
// The lab captures the microphone at `sampleRate` and calls sendAudio(Int16Array). Engines with
// `clientTurns` get commit() when the lab hears the end of a sentence; the others cut sentences
// themselves; they also get discard() for a "sentence" that turned out to be noise. DeepL and
// Gladia speak exactly the protocol live_captions_controller.js uses.

const SONIOX_WEBSOCKET_URL = "wss://stt-rt.soniox.com/transcribe-websocket";
const OPENAI_WEBSOCKET_URL = "wss://api.openai.com/v1/realtime?intent=transcription";
const MIN_COMMIT_MS = 100; // OpenAI rejects shorter commits; Canary ignores them

class Engine {
  static sampleRate = 16000;
  static clientTurns = false;

  constructor(lab, session, options) {
    this.lab = lab;
    this.session = session;
    this.options = options; // { language, openaiModel }
    this.sentences = 0;
    this.finishing = false;
  }

  nextKey() {
    this.sentences += 1;
    return `${this.constructor.id}-${this.sentences}`;
  }

  // Resolves once the socket is open; rejects if it closes before that.
  open(url, protocols) {
    return new Promise((resolve, reject) => {
      const socket = protocols ? new WebSocket(url, protocols) : new WebSocket(url);
      let opened = false;
      this.socket = socket;
      socket.onopen = () => {
        opened = true;
        resolve();
      };
      socket.onmessage = async (event) => {
        try {
          this.handle(JSON.parse(await textOf(event.data)));
        } catch (error) {
          this.lab.log(`✖ message illisible : ${error.message}`);
        }
      };
      socket.onerror = () => this.lab.log("✖ erreur websocket");
      socket.onclose = (event) => {
        if (!opened) return reject(new Error(`Connexion ${this.constructor.label} refusée (${event.code}).`));
        this.lab.engineClosed(this, event.code, event.reason);
      };
    });
  }

  send(data) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(data);
  }

  sendAudio(_int16) {}

  commit() {
    return false;
  }

  discard() {}

  // Asks the engine for its last text; the lab waits for engineEnded() or a timeout.
  finish() {
    this.finishing = true;
  }

  close() {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) this.socket.close(1000);
  }
}

class DeeplEngine extends Engine {
  static id = "deepl";
  static label = "DeepL";

  connect() {
    return this.open(`${this.session.streaming_url}?token=${encodeURIComponent(this.session.token)}`);
  }

  sendAudio(int16) {
    this.send(JSON.stringify({ source_media_chunk: { data: toBase64(int16) } }));
  }

  finish() {
    super.finish();
    this.send(JSON.stringify({ end_of_source_media: {} }));
  }

  // `concluded` segments are new final text, `tentative` the current guess for what follows. A
  // sentence is over once an update concludes text and leaves nothing tentative.
  handle(data) {
    if (data.source_transcript_update) {
      const concluded = joinSegments(data.source_transcript_update.concluded || []);
      const tentative = joinSegments(data.source_transcript_update.tentative || []);
      if (!concluded && !tentative && !this.current) return;

      if (!this.current) this.current = { key: this.nextKey(), finalText: "" };
      if (concluded) this.current.finalText = `${this.current.finalText} ${concluded}`.trim();
      const closed = Boolean(concluded) && !tentative;
      this.lab.updateLine(this.current.key, { finalText: this.current.finalText, partialText: tentative, closed });
      if (closed) this.current = null;
    } else if (data.error) {
      this.lab.engineError(data.error.error_message || "DeepL a retourné une erreur.");
    } else if (data.end_of_stream) {
      this.lab.engineEnded(this);
    }
  }
}

class GladiaEngine extends Engine {
  static id = "gladia";
  static label = "Gladia";

  connect() {
    return this.open(this.session.streaming_url); // the URL carries its own credentials
  }

  sendAudio(int16) {
    this.send(JSON.stringify({ type: "audio_chunk", data: { chunk: toBase64(int16) } }));
  }

  finish() {
    super.finish();
    this.send(JSON.stringify({ type: "stop_recording" }));
  }

  // One utterance per frame: partials replace each other until the final one.
  handle(message) {
    if (message.type === "transcript") {
      const text = (message.data?.utterance?.text || "").trim();
      if (!text) return;
      if (!this.current) this.current = this.nextKey();
      if (message.data.is_final) {
        this.lab.updateLine(this.current, { finalText: text, partialText: "", closed: true });
        this.current = null;
      } else {
        this.lab.updateLine(this.current, { partialText: text });
      }
    } else if (message.type === "error" || (!message.type && message.error)) {
      this.lab.engineError(message.data?.message || message.message || message.error?.message ||
        String(message.error || "Gladia a retourné une erreur."));
    } else if (message.type === "post_final_transcript") {
      this.lab.engineEnded(this);
    }
  }
}

class OpenAiEngine extends Engine {
  static id = "openai";
  static label = "OpenAI";
  static sampleRate = 24000; // the only rate the Realtime API accepts
  static clientTurns = true; // gpt-live-transcribe refuses server VAD

  connect() {
    this.uncommitted = 0;
    this.items = new Map();
    this.pending = 0;
    return this.open(OPENAI_WEBSOCKET_URL, ["realtime", `openai-insecure-api-key.${this.session.client_secret}`]);
  }

  sendAudio(int16) {
    this.send(JSON.stringify({ type: "input_audio_buffer.append", audio: toBase64(int16) }));
    this.uncommitted += int16.length;
  }

  commit() {
    if (this.uncommitted < (this.constructor.sampleRate * MIN_COMMIT_MS) / 1000) return false;
    this.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    this.uncommitted = 0;
    this.pending += 1;
    return true;
  }

  discard() {
    this.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
    this.uncommitted = 0;
  }

  finish() {
    super.finish();
    if (!this.commit() && !this.pending) this.lab.engineEnded(this);
  }

  // Completions are not guaranteed to arrive in order, hence one line per item_id.
  item(itemId) {
    if (!this.items.has(itemId)) {
      const key = this.nextKey();
      this.items.set(itemId, { key, text: "" });
      this.lab.updateLine(key, {}); // holds its place in the transcript
    }
    return this.items.get(itemId);
  }

  handle(event) {
    switch (event.type) {
      case "input_audio_buffer.committed":
        this.item(event.item_id);
        return;
      case "conversation.item.input_audio_transcription.delta": {
        const item = this.item(event.item_id);
        item.text += event.delta || "";
        this.lab.updateLine(item.key, { partialText: item.text.trim() });
        return;
      }
      case "conversation.item.input_audio_transcription.completed":
        this.lab.updateLine(this.item(event.item_id).key, {
          finalText: (event.transcript || "").trim(), partialText: "", closed: true
        });
        this.pending = Math.max(0, this.pending - 1);
        if (this.finishing && !this.pending) this.lab.engineEnded(this);
        return;
      case "conversation.item.input_audio_transcription.failed":
      case "error":
        this.lab.engineError(event.error?.message || event.type);
        return;
      case "session.created":
        this.lab.log(`session OpenAI · ${event.session?.audio?.input?.transcription?.model || this.session.model}`);
    }
  }
}

class SonioxEngine extends Engine {
  static id = "soniox";
  static label = "Soniox";

  async connect() {
    await this.open(SONIOX_WEBSOCKET_URL, ["soniox-api-key", this.session.api_key]);
    const config = {
      model: "stt-rt-v5",
      audio_format: "pcm_s16le",
      sample_rate: this.constructor.sampleRate,
      num_channels: 1,
      enable_endpoint_detection: true
    };
    if (this.options.language) config.language_hints = [this.options.language];
    this.send(JSON.stringify(config));
  }

  sendAudio(int16) {
    this.send(int16.buffer);
  }

  finish() {
    super.finish();
    this.send(""); // end of stream: Soniox finalizes, answers `finished` and closes
  }

  // Final tokens arrive once and are appended; non-final ones are the current guess. <end> marks
  // the end of a sentence (endpoint detection), <fin> a finalization.
  handle(message) {
    if (message.error_code) {
      this.lab.engineError(`${message.error_code} ${message.error_message || message.error_type}`);
      return;
    }
    let partial = "";
    for (const token of message.tokens || []) {
      if (token.text === "<end>" || token.text === "<fin>") {
        if (token.is_final) this.closeSentence();
      } else if (token.is_final) {
        this.sentence().finalText += token.text;
      } else {
        partial += token.text;
      }
    }
    if (partial) this.sentence();
    if (this.current) {
      this.lab.updateLine(this.current.key, {
        finalText: this.current.finalText.trim(),
        partialText: this.current.finalText ? partial : partial.trimStart()
      });
    }
    if (message.finished) {
      this.closeSentence();
      this.lab.engineEnded(this);
    }
  }

  sentence() {
    if (!this.current) this.current = { key: this.nextKey(), finalText: "" };
    return this.current;
  }

  closeSentence() {
    if (!this.current) return;
    this.lab.updateLine(this.current.key, { finalText: this.current.finalText.trim(), partialText: "", closed: true });
    this.current = null;
  }
}

// Our own server (lib/canary_live): one final per commit, numbered like the commits, plus
// provisional text for the sentence in progress.
class CanaryEngine extends Engine {
  static id = "canary";
  static label = "Canary live";
  static clientTurns = true;

  async connect() {
    this.uncommitted = 0;
    this.commits = 0;
    this.pending = 0;
    this.turnKeys = new Map();
    await this.open(this.session.websocket_url, ["canary-live", this.session.token]);
    this.send(JSON.stringify({
      language: this.options.language || "fr", sample_rate: this.constructor.sampleRate, partials: true
    }));
  }

  sendAudio(int16) {
    this.send(int16.buffer);
    this.uncommitted += int16.length;
  }

  commit() {
    if (this.uncommitted < (this.constructor.sampleRate * MIN_COMMIT_MS) / 1000) return false;
    this.send(JSON.stringify({ type: "commit" }));
    this.uncommitted = 0;
    this.commits += 1;
    this.pending += 1;
    this.lab.updateLine(this.turnKey(this.commits), {}); // holds its place in the transcript
    return true;
  }

  // The server drops the audio without spending a turn number; any provisional text already shown
  // for that turn is removed (closing a line without final text deletes it).
  discard() {
    this.send(JSON.stringify({ type: "discard" }));
    this.uncommitted = 0;
    const id = this.commits + 1;
    if (this.turnKeys.has(id)) {
      this.lab.updateLine(this.turnKeys.get(id), { finalText: "", partialText: "", closed: true });
      this.turnKeys.delete(id);
    }
  }

  finish() {
    super.finish();
    if (!this.commit() && !this.pending) this.lab.engineEnded(this);
  }

  turnKey(id) {
    if (!this.turnKeys.has(id)) this.turnKeys.set(id, this.nextKey());
    return this.turnKeys.get(id);
  }

  handle(message) {
    switch (message.type) {
      case "ready":
        this.lab.log(`Canary prêt · ${message.model} sur ${message.device} · ${message.language}`);
        return;
      case "partial":
        this.lab.updateLine(this.turnKey(message.id), { partialText: message.text });
        return;
      case "final":
        this.lab.updateLine(this.turnKey(message.id), { finalText: message.text, partialText: "", closed: true });
        this.turnKeys.delete(message.id);
        this.pending = Math.max(0, this.pending - 1);
        if (this.finishing && !this.pending) this.lab.engineEnded(this);
        return;
      case "error":
        this.lab.engineError(message.message);
    }
  }
}

export const ENGINES = Object.fromEntries(
  [DeeplEngine, GladiaEngine, OpenAiEngine, SonioxEngine, CanaryEngine].map((engine) => [engine.id, engine])
);

// Same joining as live_captions_controller.js: DeepL sometimes repeats a segment back-to-back.
function joinSegments(segments) {
  const texts = segments.map((segment) => (segment.text || "").trim()).filter(Boolean);
  return texts
    .filter((text, index) => index === 0 || text.toLowerCase() !== texts[index - 1].toLowerCase())
    .join(" ")
    .trim();
}

async function textOf(data) {
  if (typeof data === "string") return data;
  if (data instanceof Blob) return data.text();
  return new TextDecoder().decode(data);
}

function toBase64(int16) {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}
